import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

/**
 * Global Video Player Context — keeps a single YouTube iframe mounted at the App
 * root so playback survives route changes. The visual position (full overlay
 * vs. floating mini) is controlled by state, but the <iframe> DOM node is never
 * unmounted while a track is playing.
 */

const VideoPlayerCtx = createContext(null);

export const useVideoPlayer = () => {
  const ctx = useContext(VideoPlayerCtx);
  if (!ctx) throw new Error("useVideoPlayer must be used within VideoPlayerProvider");
  return ctx;
};

export const VideoPlayerProvider = ({ children }) => {
  const [queue, setQueue] = useState([]); // array of tracks
  const [idx, setIdx] = useState(0);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const [audioOn, setAudioOn] = useState(false);
  const iframeRef = useRef(null);

  const current = queue[idx] || null;

  const ytPost = useCallback((func, args = []) => {
    const w = iframeRef.current?.contentWindow;
    if (!w) return;
    w.postMessage(JSON.stringify({ event: "command", func, args }), "*");
  }, []);

  // Listen for YouTube player state changes
  useEffect(() => {
    const onMessage = (e) => {
      const iframe = iframeRef.current;
      if (!iframe || e.source !== iframe.contentWindow) return;
      let data = e.data;
      if (typeof data === "string") {
        try { data = JSON.parse(data); } catch { return; }
      }
      const info = data?.info;
      if (!info) return;
      if (typeof info.muted === "boolean") setAudioOn(!info.muted);
      if (typeof info.playerState === "number") {
        // 0=ended, 1=playing, 2=paused, 3=buffering
        if (info.playerState === 0) {
          setIdx((i) => (queue.length ? (i + 1) % queue.length : 0));
        } else if (info.playerState === 1) {
          setIsPlaying(true);
        } else if (info.playerState === 2) {
          setIsPlaying(false);
        }
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [queue.length]);

  // Poll for status + auto-unmute in the first few seconds
  useEffect(() => {
    if (!current) return;
    setAudioOn(false);
    setIsPlaying(true);
    let elapsed = 0;
    const timer = setInterval(() => {
      elapsed += 500;
      const w = iframeRef.current?.contentWindow;
      if (w) w.postMessage(JSON.stringify({ event: "listening" }), "*");
      if (elapsed >= 800 && elapsed <= 3000) {
        ytPost("unMute");
        ytPost("setVolume", [100]);
      }
      if (elapsed >= 8000) clearInterval(timer);
    }, 500);
    return () => clearInterval(timer);
  }, [current?.id, ytPost]);

  const playQueue = useCallback((tracks, startIdx = 0) => {
    setQueue(tracks || []);
    setIdx(startIdx);
    setIsMinimized(false);
  }, []);

  const playTrackInQueue = useCallback((trackId) => {
    const i = queue.findIndex((t) => t.id === trackId);
    if (i >= 0) {
      setIdx(i);
      setIsMinimized(false);
    }
  }, [queue]);

  const next = useCallback(() => {
    setIdx((i) => (queue.length ? (i + 1) % queue.length : 0));
  }, [queue.length]);

  const prev = useCallback(() => {
    setIdx((i) => (queue.length ? (i - 1 + queue.length) % queue.length : 0));
  }, [queue.length]);

  const toggle = useCallback(() => {
    if (isPlaying) ytPost("pauseVideo");
    else ytPost("playVideo");
  }, [isPlaying, ytPost]);

  const enableAudio = useCallback(() => {
    ytPost("unMute");
    ytPost("setVolume", [100]);
    ytPost("playVideo");
    setAudioOn(true);
  }, [ytPost]);

  const minimize = useCallback(() => setIsMinimized(true), []);
  const expand = useCallback(() => setIsMinimized(false), []);
  const close = useCallback(() => {
    setQueue([]);
    setIdx(0);
    setIsMinimized(false);
  }, []);

  return (
    <VideoPlayerCtx.Provider
      value={{
        queue,
        idx,
        current,
        isMinimized,
        isPlaying,
        audioOn,
        iframeRef,
        playQueue,
        playTrackInQueue,
        next,
        prev,
        toggle,
        enableAudio,
        minimize,
        expand,
        close,
      }}
    >
      {children}
    </VideoPlayerCtx.Provider>
  );
};
